import { useState, useRef, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { Track } from "@/types";

interface UsePreviewModeProps {
  currentTrack: Track | null;
  isPlaying: boolean;
  setIsPlaying: (playing: boolean) => void;
  audioRef: React.RefObject<HTMLAudioElement>;
  currentTime: number;
}

export function usePreviewMode({
  currentTrack,
  isPlaying,
  setIsPlaying,
  audioRef,
  currentTime,
}: UsePreviewModeProps) {
  const { toast } = useToast();
  const previewTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [isPreviewMode, setIsPreviewMode] = useState(false);

  // Check if this is a preview playback when track changes
  useEffect(() => {
    if (currentTrack) {
      if (currentTrack.isPreview) {
        setIsPreviewMode(true);
      } else {
        setIsPreviewMode(false);
      }
    }
  }, [currentTrack]);

  // Clean up any timers when component unmounts or track changes
  useEffect(() => {
    return () => {
      if (previewTimerRef.current) {
        clearTimeout(previewTimerRef.current);
        previewTimerRef.current = null;
      }
    };
  }, [currentTrack]);

  // Start preview timer if in preview mode
  useEffect(() => {
    if (isPreviewMode && isPlaying) {
      // Clear any existing timer
      if (previewTimerRef.current) {
        clearTimeout(previewTimerRef.current);
      }

      // Set new timer for 29 seconds
      previewTimerRef.current = setTimeout(() => {
        if (audioRef.current) {
          audioRef.current.pause();
          setIsPlaying(false);
          setIsPreviewMode(false);
        }
      }, 29000);
    }

    // Clean up timer when preview mode is disabled or playback stops
    return () => {
      if (!isPreviewMode && previewTimerRef.current) {
        clearTimeout(previewTimerRef.current);
        previewTimerRef.current = null;
      }
    };
  }, [isPreviewMode, isPlaying, toast, setIsPlaying, audioRef]);

  // Check if time exceeds 29 seconds in preview mode
  useEffect(() => {
    if (isPreviewMode && currentTime >= 29 && audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
      setIsPreviewMode(false);

      if (previewTimerRef.current) {
        clearTimeout(previewTimerRef.current);
        previewTimerRef.current = null;
      }
    }
  }, [isPreviewMode, currentTime, audioRef, setIsPlaying, toast]);

  // Enable preview mode for 29-second limit
  const enablePreviewMode = () => {
    setIsPreviewMode(true);
  };

  // Disable preview mode
  const disablePreviewMode = () => {
    setIsPreviewMode(false);
    if (previewTimerRef.current) {
      clearTimeout(previewTimerRef.current);
      previewTimerRef.current = null;
    }
  };

  return {
    isPreviewMode,
    enablePreviewMode,
    disablePreviewMode,
  };
}
